import { useState, useEffect } from "react";
import {
  getBookingDetails,
  getMeetingId,
} from "../../../ApiServices/ApiServices";
import { getEmail, getRole } from "../../../DecodeToken/DecodeToken";
import { useNavigate } from "react-router-dom";
import CodeQueueSpinner, { handleError } from "../../../Utils";
import CallDetails from "./CallDetails";
import RescheduleBooking from "./RescheduleBooking";
import CancelBooking from "./CancelBookingPopup";
import ComingSoon from "../../../Pages/ComingSoon/ComingSoon";
import { useAuth } from "../../../provider/authProvider";
import Cancelled from "./Cancelled";

const BookingPage = () => {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [dropdownValue, setDropdownValue] = useState("More Actions");
  const [bookings, setBookings] = useState([]);
  const email = getEmail();
  const role = getRole();
  const navigateTo = useNavigate();
  const [currentTime, setCurrentTime] = useState("");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isReschedulePopupVisible, setIsReschedulePopupVisible] =
    useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isCancelPopupVisible, setIsCancelPopupVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const { getUserName } = useAuth();

  const getBookings = async () => {
    try {
      const response = await getBookingDetails(email, role);
      setBookings(response.bookings); // If empty, set empty bookings
    } catch (error) {
      return handleError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Dashboard - Bookings";
    if (email) {
      getBookings();
    }
  }, [email, role]);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12; // Convert 24-hour format to 12-hour format
      const formattedTime = `${String(hours).padStart(
        2,
        "0"
      )}:${minutes} ${ampm}`;
      setCurrentTime(formattedTime);
    };

    updateTime();
    const timer = setInterval(updateTime, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const startMeeting = async (id) => {
    try {
      const meetingIdResponse = await getMeetingId(id);
      if (meetingIdResponse && meetingIdResponse.meetingId) {
        // Pass meetingId as path parameter and id as query parameter
        navigateTo(
          `/room/?meetingId=${
            meetingIdResponse.meetingId
          }&id=${id}&name=${getUserName()}&role=${role}`
        );
      }
      // navigateTo(`/room/${meetingId.meetingId}`);
    } catch (error) {
      return handleError(error?.response?.data?.message);
    }
  };

  const handlePopupVisibility = (booking) => {
    setSelectedBooking(booking);
    setIsPopupVisible(true);
    setDropdownValue("More Actions"); // Reset dropdown to default value when opening popup
  };

  const closePopup = () => {
    setIsPopupVisible(false);
    setSelectedBooking(null);
    setDropdownValue("More Actions"); // Reset dropdown to default value after closing popup
  };
  const handleReschedulePopupVisibility = (booking) => {
    setSelectedBooking(booking);
    setIsReschedulePopupVisible(true); // Show Reschedule popup
    setDropdownValue("More Actions"); // Reset dropdown to default value when opening popup
  };
  const closeReschedulePopup = () => {
    setIsReschedulePopupVisible(false);
    setSelectedBooking(null);
  };

  const handleCancelPopupVisibility = (booking) => {
    setSelectedBooking(booking);
    setIsCancelPopupVisible(true); // Show cancel popup
    setDropdownValue("More Actions");
  };

  const closeCancelPopup = () => {
    setIsCancelPopupVisible(false); // Close cancel popup
    setSelectedBooking(null);
  };

  return (
    <div className="container mx-auto p-1 text-left">
      {/* First Container: Heading, Buttons, and HR */}
      <div className="flex flex-col items-start mb-4">
        <h1 className="text-3xl font-semibold mb-2">Bookings</h1>

        {/* Button */}
        <div className="flex gap-4 mb-4">
          <button className="secondary-btn py-2 px-6 rounded">1:1 Calls</button>
        </div>

        {/* Horizontal Line */}
        <hr className="w-full border-t border-gray-300" />
      </div>

      {/* Tabs for Upcoming and Completed */}
      <div className="flex gap-3 mb-1">
        <div
          onClick={() => handleTabClick("upcoming")}
          className={`cursor-pointer ${
            activeTab === "upcoming"
              ? "text-black font-bold border-b-2 border-black"
              : "text-darkgray"
          }`}
        >
          Upcoming
        </div>
        <div
          onClick={() => handleTabClick("completed")}
          className={`cursor-pointer ${
            activeTab === "completed"
              ? "text-black font-bold border-b-2 border-black"
              : "text-darkgray"
          }`}
        >
          Completed
        </div>
        {/* <div
          onClick={() => handleTabClick("cancelled")}
          className={`cursor-pointer ${
            activeTab === "cancelled"
              ? "text-black font-bold border-b-2 border-black"
              : "text-darkgray"
          }`}
        >
          Cancelled
        </div> */}
      </div>

      {loading ? (
        <div>
          <CodeQueueSpinner />
        </div>
      ) : (
        <div
          className="max-h-[440px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-200 "
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {/* Second Container: Content based on Active Tab */}
          {activeTab === "upcoming" && (
            <div>
              {bookings?.filter((item) => item?.status === "pending").length ===
                0 && (
                <div className="mt-4 md:ml-4 text-center text-gray-600 font-bold">
                  No upcoming bookings.
                </div>
              )}
              {bookings?.map((item, index) => {
                if (item?.status !== "pending") return null;

                // Check if the button should be enabled
                const now = new Date();
                const sessionStart = new Date(
                  `${item.scheduledOn} ${item.time}`
                );
                const sessionEnd = new Date(
                  sessionStart.getTime() + item.duration * 60000
                );
                // Enable button only if current time is within session window
                const isButtonEnabled =
                  now >= sessionStart && now <= sessionEnd;

                return (
                  <div className="mt-4 md:ml-4" key={index}>
                    {/* Heading for Upcoming Tab */}
                    <h2 className="text-md font-bold text-black mb-2">
                      {item?.day} {item?.scheduledOn}
                    </h2>

                    {/* Container for Upcoming Bookings */}
                    <div className="flex gap-0 justify-center">
                      {/* Left Column - Always visible and full width on small screens */}
                      <div className="w-full sm:w-1/2 p-1 border border-gray-300 h-auto rounded-md bg-slate-100">
                        {/* Row with Time and Rupee */}
                        <div className="flex flex-wrap justify-between items-center mb-2">
                          <p className="text-black">{item?.time}</p>
                          <div className="bg-yellow-800 text-white px-4 py-2 rounded-full font-bold text-sm sm:text-base sm:text-right w-20 sm:w-auto text-center">
                            ₹
                            {item.amount?.service_amount +
                              item.amount?.platform_fee +
                              item.amount?.additional_amount}
                          </div>
                        </div>

                        {/* Horizontal Rule */}
                        <hr className="border-t border-gray-300 my-2" />

                        {/* Name and Description */}
                        <p className="text-black font-bold">
                          {role === "user"
                            ? item?.mentor_name
                            : role === "mentor"
                            ? item?.user_name
                            : null}
                        </p>
                        <p className="text-gray-600">
                          {item.session_title} ({item?.duration} mins)
                        </p>

                        {/* Bottom Section (flex-wrap) */}
                        <div className="flex flex-wrap gap-2 justify-end sm:items-center sm:p-1">
                          {role === "mentor" && (
                            <select
                              value={dropdownValue}
                              onChange={(e) => {
                                setDropdownValue(e.target.value);
                                if (e.target.value === "Call Details") {
                                  handlePopupVisibility(item); // Pass 'item' here
                                } else if (e.target.value === "Reschedule") {
                                  handleReschedulePopupVisibility(item); // Pass 'item' here
                                } else if (e.target.value === "Cancel") {
                                  handleCancelPopupVisibility(item);
                                }
                              }}
                              className="p-2 border rounded-md w-full sm:w-auto max-w-full mb-1"
                            >
                              <option>More Actions</option>
                              <option>Call Details</option>
                              <option>Reschedule</option>
                              <option>Cancel</option>
                            </select>
                          )}

                          {/* Start Call Button */}
                          <button
                            onClick={() => startMeeting(item?._id)}
                            disabled={!isButtonEnabled}
                            className={`px-6 py-2 rounded-md text-white w-full sm:w-auto mb-1 ${
                              isButtonEnabled
                                ? "bg-green-600"
                                : "bg-green-200 cursor-not-allowed"
                            }`}
                          >
                            Start Call
                          </button>
                        </div>
                      </div>

                      {/* Right Column - Hidden on small screens */}
                      <div className="hidden sm:block sm:w-1/2  border border-gray-300 h-auto pt-2 rounded-md bg-slate-100">
                        {/* You can add content for the right column here */}
                        <h3 className="text-black font-bold ml-2">
                          Price Breakdown
                        </h3>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-black ml-2 text-sm ">
                            Service amount
                          </p>
                          <p className="text-black mr-2 text-sm">
                            ₹ {item.amount?.service_amount}
                          </p>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-black ml-2 text-sm">
                            Platform fee
                          </p>
                          <p className="text-black mr-2 text-sm">
                            ₹ {item.amount?.platform_fee}
                          </p>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-black ml-2 text-sm">
                            Additional amount
                          </p>
                          <p className="text-black mr-2 text-sm">
                            ₹ {item.amount?.additional_amount}
                          </p>
                        </div>
                        <hr className="border-t border-gray-300 mt-4" />
                        {/* Total and ₹ 1 in the same row at the bottom, no margin */}
                        <div className="flex justify-between items-center ">
                          <p className="text-black text-md font-bold ml-2">
                            Total
                          </p>{" "}
                          {/* Increased font size and bold */}
                          <p className="font-bold text-black text-md mr-2">
                            ₹{" "}
                            {item.amount?.service_amount +
                              item.amount?.platform_fee +
                              item.amount?.additional_amount}
                          </p>{" "}
                          {/* Increased font size */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "completed" && (
            <div>
              {bookings?.filter((item) => item?.status === "completed")
                .length === 0 && (
                <div className="mt-4 md:ml-4 text-center text-gray-600 font-bold">
                  Not bookings completed yet!.
                </div>
              )}
              {bookings?.map((item, index) => {
                if (item?.status !== "completed") return null;
                return (
                  <div className="mt-4 md:ml-4" key={index}>
                    {/* Heading for Completed Tab */}
                    <p className="text-md font-bold text-gray mb-2">
                      {item?.day} {item?.scheduledOn}
                    </p>

                    {/* Container for Completed Bookings */}
                    <div className="flex gap-0 justify-center ">
                      {/* Left Column - Always visible and full width on small screens */}
                      <div className="w-full sm:w-1/2 p-1 border border-gray-300 h-auto bg-slate-100 rounded-md">
                        {/* Row with Time and Rupee */}
                        <div className="flex flex-wrap justify-between items-center mb-2">
                          <p className="text-black ">{item?.time}</p>
                          <div className="bg-yellow-800 text-white px-4 py-2 rounded-full font-bold text-sm sm:text-base sm:text-right w-20 sm:w-auto text-center">
                            ₹
                            {item.amount?.service_amount +
                              item.amount?.platform_fee +
                              item.amount?.additional_amount}
                          </div>
                        </div>

                        {/* Horizontal Rule */}
                        <hr className="border-t border-gray-300 my-2" />

                        {/* Name and Description */}
                        <p className="text-black font-bold">
                          {role === "user"
                            ? item?.mentor_name
                            : role === "mentor"
                            ? item?.user_name
                            : null}
                        </p>
                        <p className="text-gray-600">
                          {item.session_title} ({item?.duration} mins)
                        </p>

                        {/* Bottom Section (flex-wrap) */}
                        <div className="mt-4 flex flex-wrap gap-2 justify-end sm:items-center sm:p-1">
                          <div className="p-2 border rounded-md w-full sm:w-auto max-w-full mb-1 flex justify-center items-center text-green-800 bg-green-100 font-bold">
                            Completed
                          </div>
                        </div>
                      </div>

                      {/* Right Column - Hidden on small screens */}
                      <div className="hidden sm:block sm:w-1/2  border border-gray-300 h-auto pt-2 rounded-md bg-slate-100">
                        {/* You can add content for the right column here */}
                        <h3 className="text-black font-bold ml-2">
                          Price Breakdown
                        </h3>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-black ml-2 text-sm ">
                            Service amount
                          </p>
                          <p className="text-black mr-2 text-sm">
                            ₹ {item.amount?.service_amount}
                          </p>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-black ml-2 text-sm">
                            Platform fee
                          </p>
                          <p className="text-black mr-2 text-sm">
                            ₹ {item.amount?.platform_fee}
                          </p>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-black ml-2 text-sm">
                            Additional amount
                          </p>
                          <p className="text-black mr-2 text-sm">
                            ₹ {item.amount?.additional_amount}
                          </p>
                        </div>
                        <hr className="border-t border-gray-300 mt-4" />
                        {/* Total and ₹ 1 in the same row at the bottom, no margin */}
                        <div className="flex justify-between items-center ">
                          <p className="text-black text-md font-bold ml-2">
                            Total
                          </p>{" "}
                          {/* Increased font size and bold */}
                          <p className="font-bold text-black text-md mr-2">
                            ₹{" "}
                            {item.amount?.service_amount +
                              item.amount?.platform_fee +
                              item.amount?.additional_amount}
                          </p>{" "}
                          {/* Increased font size */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "cancelled" && (
            <Cancelled setActiveTab={setActiveTab}
            />
          )}
        </div>
      )}
      {/* Popup for Call Details */}
      {isPopupVisible && selectedBooking && (
        <CallDetails item={selectedBooking} closePopup={closePopup} />
      )}
      {/* Reschedule Booking Modal */}
      {isReschedulePopupVisible && selectedBooking && (
        <RescheduleBooking
          item={selectedBooking}
          closePopup={closeReschedulePopup}
          getBookings={getBookings}
        />
      )}
      {/* Popup for Cancel Booking */}
      {isCancelPopupVisible && selectedBooking && (
        // <CancelBooking item={selectedBooking} closePopup={closeCancelPopup} />
        <ComingSoon
          isVisible={isCancelPopupVisible}
          setIsVisible={setIsCancelPopupVisible}
        />
      )}
    </div>
  );
};

export default BookingPage;
